import Layout from "../Layout/Layout";
import { useEffect, useState } from "react";
import { getPages } from "./utilities/ApiCalls";
import { Helmet } from "react-helmet-async";
import { setPageTitle } from "../utils/titleUtils";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { Sparkles, Compass, Globe, Heart } from "lucide-react";

export default function OurVision() {
  const location = useLocation();
  const defaultVision = {
    title: "Our Vision & Mission",
    content: "At StoryTent, our vision is to cultivate a worldwide community of readers, writers, and poets, transcending borders and embracing the boundless realm where storytelling knows no constraints. We aspire to empower every individual to create, express, and celebrate their stories freely."
  };
  const [about, setAbout] = useState(defaultVision);

  useEffect(() => {
    setPageTitle(location.pathname);
    getPages({ 'title': 'our vision & mission' }).then((response) => {
      if (response && response.data && response.data.content) {
        setAbout(response.data);
      }
    }).catch((error) => {
      console.error("Error fetching vision content:", error);
    });
  }, [location]);

  return (
    <Layout>
      <Helmet>
        <title>Our Vision & Mission | StoryTent</title>
        <meta name="description" content="Discover StoryTent's vision and mission to empower storytellers, poets, and artists worldwide to share their voices and connect with global audiences." />
        <meta property="og:title" content="Our Vision & Mission | StoryTent" />
        <meta property="og:description" content="Discover StoryTent's vision and mission to empower storytellers, poets, and artists worldwide." />
        <meta property="og:url" content="https://www.storytent.in/our-vision" />
        <link rel="canonical" href="https://www.storytent.in/our-vision" />
      </Helmet>

      <PageWrapper>
        <div className="container">
          <HeroCard>
            <IconBadge>
              <Compass size={30} color="#2dd4bf" />
            </IconBadge>
            <MainTitle>{about.title || "Our Vision & Mission"}</MainTitle>
            <HeroDescription>{about.content || defaultVision.content}</HeroDescription>
          </HeroCard>

          <PillarsGrid>
            <PillarCard>
              <PillarIconWrap>
                <Sparkles size={24} color="#f59e0b" />
              </PillarIconWrap>
              <PillarTitle>Empowering Every Voice</PillarTitle>
              <PillarText>
                We believe that every soul is a born creator. We provide the digital and live stage so no masterpiece languishes in obscurity.
              </PillarText>
            </PillarCard>

            <PillarCard>
              <PillarIconWrap>
                <Globe size={24} color="#38bdf8" />
              </PillarIconWrap>
              <PillarTitle>Borderless Connection</PillarTitle>
              <PillarText>
                Connecting wordsmiths and appreciative audiences across geographic boundaries and celebrating diverse multilingual tapestries.
              </PillarText>
            </PillarCard>

            <PillarCard>
              <PillarIconWrap>
                <Heart size={24} color="#ec4899" />
              </PillarIconWrap>
              <PillarTitle>Recognition & Growth</PillarTitle>
              <PillarText>
                Facilitating discovery, mentorship, and opportunities through open mics, contests, and collaborative community events.
              </PillarText>
            </PillarCard>
          </PillarsGrid>
        </div>
      </PageWrapper>
    </Layout>
  );
}

const PageWrapper = styled.div`
  background: transparent;
  color: var(--color-text, #ffffff);
  padding: 30px 0 60px;
  min-height: 80vh;
`;

const HeroCard = styled.div`
  background: var(--color-surface, #141f1e);
  border: 1px solid var(--color-border, rgba(255, 255, 255, 0.1));
  border-radius: 16px;
  padding: 36px 30px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
  text-align: center;
  max-width: 920px;
  margin: 0 auto 36px;
`;

const IconBadge = styled.div`
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: rgba(45, 212, 191, 0.12);
  border: 1px solid rgba(45, 212, 191, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
`;

const MainTitle = styled.h1`
  color: var(--color-text, #ffffff);
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const HeroDescription = styled.p`
  color: var(--color-text-secondary, #cbd5e1);
  font-size: 1.12rem;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto;
  text-align: justify;

  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: left;
  }
`;

const PillarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 920px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PillarCard = styled.div`
  background: var(--color-surface, #141f1e);
  border: 1px solid var(--color-border, rgba(255, 255, 255, 0.1));
  border-radius: 14px;
  padding: 24px 20px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: rgba(45, 212, 191, 0.3);
  }
`;

const PillarIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const PillarTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--color-text, #ffffff);
  margin-bottom: 8px;
`;

const PillarText = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary, #94a3b8);
  line-height: 1.6;
  margin: 0;
`;